import { use, useMemo } from 'react'

import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ChatContext } from '@app/context'
import { getBaseInfo, getDateFnsLocale } from '@app/helpers'
import {
  useTranslatedMessage,
  useTranslatedSystemMessage,
  useUser,
} from '@app/hooks'
import { ConversationItem, TourTime } from '@app/models'
import {
  getConversationDate,
  getLocalDate,
  getTourTime,
  scrollToBottom,
  sendEventOpenConversation,
} from '@app/utils'

export const useListItem = (conversation: ConversationItem) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const locale = getDateFnsLocale(i18n.language)
  const { currentUser } = useUser()
  const { state } = use(ChatContext)
  const conversationSubject = conversation.fullSubject || conversation.subject || ''

  const replaceLineBreaks = (text?: string | null) =>
    (text || '').replace(/\n/g, ' ').replace(/<br\s*\/?>/gi, ' ')

  const formatTourDate = (date?: string, time?: TourTime[]) => {
    const formattedDate =
      date &&
      !isNaN(Date.parse(date.toString())) &&
      format(date, 'd MMMM', {
        locale: locale,
      })

    const formattedTime =
      (time && getTourTime('HH:mm:ss', locale, time)) ||
      (formattedDate && t('Chat.Inbox.Tour.AnyTimeslot')) ||
      ''

    return `${formattedDate ? formattedDate : t('Chat.Inbox.Tour.AsSoonAsPossible')} ${formattedTime}`
  }

  const date = useMemo(
    () =>
      getConversationDate(
        getLocalDate(conversation.updateDate).toISOString(),
        locale
      ),
    [conversation.updateDate, locale]
  )

  const firstMessage = conversation.messages[0]
  const translatedSystemMessage = useTranslatedSystemMessage(
    firstMessage,
    conversation
  )
  const translatedMessage = useTranslatedMessage(firstMessage, conversation)

  const hasFollowUpActions = useMemo(() => {
    const currentParticipant = conversation.participants.find(
      (participant) => participant.userId === currentUser.id
    )

    if (!currentParticipant) {
      return false
    }

    return conversation.suggestedActions?.some(
      (action) =>
        action.status === 'Pending' &&
        action.toParticipantId === currentParticipant.id
    )
  }, [conversation.participants, conversation.suggestedActions, currentUser.id])

  const message = useMemo(() => {
    if (hasFollowUpActions) {
      return t('Chat.FollowUpActions.Title')
    }

    const msg = conversation.messages[0]

    if (msg?.source === 'System') {
      return replaceLineBreaks(
        translatedSystemMessage ||
          msg?.content?.text ||
          conversationSubject
      )
    }

    const participant =
      conversation.participants?.find(
        (participant) => participant.id == msg?.fromParticipantId
      ) || null
    const { fullName } = getBaseInfo(state, participant, conversation)

    const youPrefix =
      participant?.userId === currentUser.id
        ? `${t('Chat.Message.You')}: `
        : undefined

    const userPrefix = youPrefix || `${fullName}: ` || ''

    if (msg?.source == 'Email' && !translatedMessage?.length) {
      return `${userPrefix}${t('Chat.Message.RepliedByEmail').toLowerCase()}`
    }

    if (msg?.source == 'Lead' && msg?.metadata?.leadType === 'Tour') {
      return `${t('Chat.Conversation.List.TourRequestPreview')}
              ${formatTourDate(conversation.context?.tourDate, conversation.context?.tourTime)}`
    }

    return `${youPrefix || ''}${replaceLineBreaks(translatedMessage || conversationSubject)}`
  }, [
    hasFollowUpActions,
    conversation.context?.tourDate,
    conversation.context?.tourTime,
    conversation.fullSubject,
    conversation.subject,
    conversation.messages,
    conversation.participants,
    currentUser.id,
    translatedMessage,
    translatedSystemMessage,
  ])

  const handleConversationClick = () => {
    scrollToBottom()

    setTimeout(() => {
      sendEventOpenConversation()
      navigate(`/messages/${conversation.id}${location.search}`)
    }, 0)
  }

  return {
    date,
    message,
    handleConversationClick,
  }
}
